import { useMemo } from 'react';

import { useCurrentCountryConfiguration } from '@rover/rsdk/src/modules/Data/Core/CountryConfiguration';

import { useStatsigClient } from '../StatsigLib';

export const HIGHER_REFERRER_INCENTIVES_US_EXPERIMENT = 'experiment_higher_referrer_incentives_us';
export const HIGHER_REFERRER_INCENTIVES_UK_EXPERIMENT = 'experiment_higher_referrer_incentives_uk';
const HIGHER_REFERRER_INCENTIVES_US_GATE = 'rollout_higher_referrer_incentives_us';
const HIGHER_REFERRER_INCENTIVES_UK_GATE = 'rollout_higher_referrer_incentives_uk';

// Mirrors the backend per-geo min-booking constants. USD is the default; test only buckets USD/GBP.
const HIGHER_REFERRER_INCENTIVES_MIN_BOOKING_VALUE_BY_CURRENCY: Record<string, string> = {
  USD: '$20',
  GBP: '£15',
};

/**
 * True only when the user's currency maps to a geo (USD->US, GBP->UK) whose rollout gate is on AND
 * whose experiment buckets them into "variant". In the variant arm referrer and friend earn
 * different amounts, so referral surfaces must swap their symmetric "both get X" copy for
 * asymmetric copy.
 *
 * Mirrors marketing/ambassador/flags.py: read the gate first and only read the matching geo's
 * experiment, since getExperiment logs an exposure — checking it on out-of-scope users (wrong
 * currency, or gate off) would contaminate the experiment metrics.
 */
export const useInHigherReferrerIncentivesVariant = (): boolean => {
  const { data: countryConfiguration } = useCurrentCountryConfiguration();
  const { client } = useStatsigClient();
  const currencyCode = countryConfiguration?.currencyCode;

  return useMemo(() => {
    if (currencyCode === 'USD') {
      if (!client.checkGate(HIGHER_REFERRER_INCENTIVES_US_GATE)) return false;
      return (
        client.getExperiment(HIGHER_REFERRER_INCENTIVES_US_EXPERIMENT).get('bucket', 'control') ===
        'variant'
      );
    }
    if (currencyCode === 'GBP') {
      if (!client.checkGate(HIGHER_REFERRER_INCENTIVES_UK_GATE)) return false;
      return (
        client.getExperiment(HIGHER_REFERRER_INCENTIVES_UK_EXPERIMENT).get('bucket', 'control') ===
        'variant'
      );
    }
    return false;
  }, [client, currencyCode]);
};

// Min-booking threshold the friend must hit for the referrer to earn the payout ($20/£15).
export const getHigherReferrerIncentivesMinBookingValue = (currencyCode?: string): string =>
  HIGHER_REFERRER_INCENTIVES_MIN_BOOKING_VALUE_BY_CURRENCY[currencyCode ?? 'USD'] ?? '$20';

// Promo end date per geo. UK reads day-month, US reads month-day; copy is English-only so we
// format the order per currency rather than localizing. USD is the default.
const HIGHER_REFERRER_INCENTIVES_PROMO_END_DATE_BY_CURRENCY: Record<string, string> = {
  USD: 'September 6, 2026',
  GBP: '6 September 2026',
};

export const getHigherReferrerIncentivesPromoEndDate = (currencyCode?: string): string =>
  HIGHER_REFERRER_INCENTIVES_PROMO_END_DATE_BY_CURRENCY[currencyCode ?? 'USD'] ??
  'September 6, 2026';
